<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { ALL_CLASSES, type ClassSummary } from '../composables/use-accounts-tree.ts'
import type { AccountRow } from '../internal/account-tree.ts'

type AccountClass = AccountRow['accountClass']

const props = defineProps<{
  summaries: readonly ClassSummary[]
  classFilter: AccountClass | typeof ALL_CLASSES
}>()

const emit = defineEmits<{
  'update:classFilter': [value: AccountClass | typeof ALL_CLASSES]
}>()

const { t } = useI18n()

const chips = computed<readonly { value: AccountClass | typeof ALL_CLASSES; label: string; count: number }[]>(
  () => {
    const total = props.summaries.reduce((sum, summary) => sum + summary.accountCount, 0)
    return [
      { value: ALL_CLASSES, label: t('accounts.allClasses'), count: total },
      ...props.summaries.map((summary) => ({
        value: summary.root.accountClass,
        label: t(`accounts.classes.${summary.root.accountClass}`),
        count: summary.accountCount,
      })),
    ]
  },
)

const chipClass = (selected: boolean) =>
  selected
    ? 'border-[color:var(--color-border-control)] bg-[color:var(--color-surface-raised)] text-[color:var(--color-text)]'
    : 'border-[color:var(--color-border)] bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]'
</script>

<template>
  <div
    role="group"
    :aria-label="t('accounts.accountClass')"
    class="flex flex-wrap items-center gap-[var(--space-inline)]"
  >
    <button
      v-for="chip in chips"
      :key="chip.value"
      type="button"
      :aria-pressed="classFilter === chip.value"
      class="flex min-h-[var(--density-control)] items-center gap-[var(--space-inline)] rounded-full border px-[var(--space-group)] text-[length:var(--text-step-2)] transition-colors duration-[var(--motion-fast)]"
      :class="chipClass(classFilter === chip.value)"
      @click="emit('update:classFilter', chip.value)"
    >
      {{ chip.label }}
      <span class="figure tabular-nums opacity-80">{{ chip.count }}</span>
    </button>
  </div>
</template>
